import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import {
  Plane,
  Shield,
  Clock,
  CheckCircle,
  Phone,
  Mail,
  FileText,
  Users,
  Globe,
  Lock,
  Search,
  ClipboardCheck,
  Truck,
  Building2,
  Anchor,
  ChevronRight,
  Send,
  Target,
  Briefcase,
  Eye,
  Award,
  Waves,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AircraftBrokeragePage = () => {
  const [buyLoading, setBuyLoading] = useState(false);
  const [sellLoading, setSellLoading] = useState(false);
  
  const [buyForm, setBuyForm] = useState({
    name: "",
    organization: "",
    email: "",
    phone: "",
    aircraft_type: "",
    budget: "",
    timeline: "",
    mission_description: "",
    contact_method: "",
  });

  const [sellForm, setSellForm] = useState({
    name: "",
    organization: "",
    email: "",
    phone: "",
    aircraft_type: "",
    year: "",
    hours: "",
    asking_price: "",
    location: "",
    notes: "",
  });

  const handleBuySubmit = async (e) => {
    e.preventDefault();
    setBuyLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: buyForm.name,
        email: buyForm.email,
        phone: buyForm.phone,
        company: buyForm.organization,
        inquiry_type: "contracting",
        message: `AIRCRAFT ACQUISITION REQUEST\n\nAircraft Type: ${buyForm.aircraft_type}\nBudget Range: ${buyForm.budget}\nTimeline: ${buyForm.timeline}\nPreferred Contact: ${buyForm.contact_method}\n\nMission Description:\n${buyForm.mission_description}`,
      });
      toast.success("Aircraft request submitted. AMR Global will respond within 24-48 hours.");
      setBuyForm({ name: "", organization: "", email: "", phone: "", aircraft_type: "", budget: "", timeline: "", mission_description: "", contact_method: "" });
    } catch (error) {
      toast.error("Submission failed. Please contact us directly.");
    } finally {
      setBuyLoading(false);
    }
  };

  const handleSellSubmit = async (e) => {
    e.preventDefault();
    setSellLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: sellForm.name,
        email: sellForm.email,
        phone: sellForm.phone,
        company: sellForm.organization,
        inquiry_type: "contracting",
        message: `AIRCRAFT SALE INQUIRY\n\nAircraft Type: ${sellForm.aircraft_type}\nYear: ${sellForm.year}\nTotal Hours: ${sellForm.hours}\nAsking Price: ${sellForm.asking_price}\nLocation: ${sellForm.location}\n\nNotes:\n${sellForm.notes}`,
      });
      toast.success("Aircraft listing inquiry submitted. We'll be in touch shortly.");
      setSellForm({ name: "", organization: "", email: "", phone: "", aircraft_type: "", year: "", hours: "", asking_price: "", location: "", notes: "" });
    } catch (error) {
      toast.error("Submission failed. Please contact us directly.");
    } finally {
      setSellLoading(false);
    }
  };

  // Brokerage categories
  const brokerageCategories = [
    {
      icon: Plane,
      title: "Private Jet Brokerage",
      description: "Light jets to heavy cabin aircraft sourcing and acquisition support for private and corporate clients.",
    },
    {
      icon: Truck,
      title: "Cargo & Utility Aircraft",
      description: "Cargo-capable aircraft sourcing for logistics, freight, and utility mission requirements.",
    },
    {
      icon: Target,
      title: "Helicopter Brokerage",
      description: "Rotorcraft acquisition support for private, commercial, and mission-specific applications.",
    },
    {
      icon: Shield,
      title: "Government & Contractor Support",
      description: "Aircraft sourcing and placement support for government agencies and defense contractors. Government and contractor inquiries welcome.",
    },
    {
      icon: Waves,
      title: "Amphibious Aircraft",
      description: "Water-capable and amphibious platform sourcing for coastal, lake, and remote-area access missions.",
    },
    {
      icon: Clock,
      title: "Lease & Charter Placement",
      description: "Short-term and long-term lease arrangements and charter placement coordination through vetted partners.",
    },
  ];

  // Acquisition process
  const acquisitionProcess = [
    { step: "01", title: "Requirements & Mission Fit", description: "Define operational needs, mission profile, and aircraft specifications." },
    { step: "02", title: "Market Search & Shortlist", description: "Access national and international aircraft opportunities through partner network." },
    { step: "03", title: "Due Diligence & History Review", description: "Comprehensive background, maintenance records, and ownership history verification." },
    { step: "04", title: "Inspection Coordination", description: "Pre-purchase inspection coordination through qualified aviation partners." },
    { step: "05", title: "Delivery Planning & Support", description: "Transition support, delivery coordination, and readiness planning." },
  ];

  // Mission ecosystem
  const missionEcosystem = [
    {
      icon: Clock,
      title: "Critical Medical & Time-Sensitive Delivery",
      description: "Aircraft sourcing support for urgent logistics and time-critical delivery missions.",
      link: "/medical-delivery",
    },
    {
      icon: Shield,
      title: "Government & Contractor Support",
      description: "Aircraft placement and acquisition support for federal, state, and contractor missions. Government and contractor inquiries welcome.",
      link: "/government-contracting",
    },
    {
      icon: Truck,
      title: "Cargo & Vehicle-Capable Missions",
      description: "Cargo aircraft sourcing for freight, equipment transport, and logistics operations.",
      link: "/logistics",
    },
    {
      icon: Briefcase,
      title: "Long-Term Fleet Planning",
      description: "Strategic aircraft acquisition planning for growing operations and fleet expansion.",
      link: "/contact",
    },
  ];

  return (
    <div className="min-h-screen" data-testid="aircraft-brokerage-page">
      {/* HERO SECTION */}
      <section
        className="relative min-h-[85vh] flex items-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1540962351504-03099e0a754b?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="brokerage-hero"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a365d]/95 via-[#1a365d]/80 to-[#1a365d]/60"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">
              AIRCRAFT BROKERAGE & ACQUISITION
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
              MISSION-READY<br />
              <span className="text-[#c5a04a]">AVIATION ASSETS</span>
            </h1>
            <p className="text-lg text-slate-200 mb-4 leading-relaxed">
              AMR Global Holdings LLC sources and supports acquisition of private jets, 
              cargo aircraft, helicopters, amphibious platforms, and special-mission 
              aircraft for commercial, private, and government-adjacent missions.
            </p>
            <p className="text-slate-300 text-sm mb-8">
              All inquiries handled professionally and confidentially.
            </p>

            <div className="flex flex-wrap gap-4 mb-8">
              <a href="#request-aircraft">
                <Button className="btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm" data-testid="hero-request-btn">
                  <Plane className="w-4 h-4 mr-2" />
                  Request Aircraft
                </Button>
              </a>
              <a href="#sell-aircraft">
                <Button variant="outline" className="btn-tactical border-2 border-white text-white hover:bg-white hover:text-[#1a365d] text-sm" data-testid="hero-sell-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  Sell Your Aircraft
                </Button>
              </a>
              <Link to="/contact">
                <Button variant="outline" className="btn-tactical border-2 border-[#c5a04a] text-[#c5a04a] hover:bg-[#c5a04a] hover:text-[#1a365d] text-sm" data-testid="hero-contact-btn">
                  <Phone className="w-4 h-4 mr-2" />
                  Speak With AMR Global
                </Button>
              </Link>
            </div>

            <div className="flex flex-wrap gap-4 text-white text-sm">
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#c5a04a]" />
                Veteran-Owned
              </span>
              <span className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#c5a04a]" />
                Discreet
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#c5a04a]" />
                Mission-Ready
              </span>
            </div>
            <p className="text-slate-400 text-xs mt-4">
              Government and contractor inquiries welcome.
            </p>
          </div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="py-16 bg-white border-b border-slate-200" data-testid="who-we-are">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">WHO WE ARE</p>
          <p className="text-slate-700 text-lg leading-relaxed mb-4">
            AMR Global Holdings LLC is a Veteran-Owned aviation infrastructure company 
            providing aircraft sourcing, brokerage support, and acquisition coordination 
            across private, commercial, cargo, and government-adjacent missions. We connect 
            clients to vetted aircraft opportunities through a national and international 
            partner network.
          </p>
          <p className="text-slate-500 text-sm">
            All inquiries handled professionally and confidentially.
          </p>
        </div>
      </section>

      {/* FULL-SPECTRUM BROKERAGE */}
      <section className="py-20 bg-slate-50" data-testid="brokerage-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">CAPABILITIES</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d]">FULL-SPECTRUM BROKERAGE</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {brokerageCategories.map((category, index) => (
              <Card key={index} className="card-tactical h-full" data-testid={`brokerage-category-${index}`}>
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-[#1a365d] rounded-sm flex items-center justify-center mb-4">
                    <category.icon className="w-7 h-7 text-[#c5a04a]" />
                  </div>
                  <h3 className="font-heading text-lg text-[#1a365d] mb-2">{category.title}</h3>
                  <p className="text-slate-600 text-sm">{category.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ICON A5 AMPHIBIOUS HIGHLIGHT */}
      <section className="py-16 bg-white border-y border-slate-200" data-testid="icon-a5-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="bg-gradient-to-br from-[#1a365d] to-[#243b53] rounded-sm p-8 flex items-center justify-center min-h-[300px]">
                <div className="text-center">
                  <Waves className="w-24 h-24 text-[#c5a04a] mx-auto mb-4 opacity-60" />
                  <p className="text-white font-heading text-xl">ICON A5</p>
                  <p className="text-slate-300 text-sm">Amphibious Aircraft</p>
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-[#c5a04a] px-4 py-2 rounded-sm">
                <span className="font-heading text-[#1a365d] text-sm">WATER-CAPABLE</span>
              </div>
            </div>
            <div>
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-xs mb-2">FEATURED AMPHIBIOUS OPTION</p>
              <h3 className="font-heading text-2xl md:text-3xl text-[#1a365d] mb-4">
                ICON A5 Amphibious Aircraft — Water-Capable Access
              </h3>
              <p className="text-slate-600 leading-relaxed mb-6">
                For coastal, lake, and remote-area access needs, amphibious platforms can 
                provide flexible routing options. AMR Global can support sourcing and partner 
                coordination for amphibious aircraft solutions such as the ICON A5 
                (mission fit dependent).
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#c5a04a] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Amphibious / water-capable operations (where permitted)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#c5a04a] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Short-hop and access flexibility (mission dependent)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#c5a04a] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Coordination support through vetted partners</span>
                </li>
              </ul>
              <div className="flex flex-wrap gap-4">
                <a href="#request-aircraft">
                  <Button className="btn-tactical btn-primary text-xs" data-testid="icon-request-btn">
                    Request Amphibious Aircraft Options
                  </Button>
                </a>
                <Link to="/contact">
                  <Button variant="outline" className="btn-tactical btn-outline text-xs" data-testid="icon-training-btn">
                    Ask About Amphibious Training
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ACQUISITION PROCESS */}
      <section className="py-20 bg-[#1a365d]" data-testid="acquisition-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">PROCESS</p>
            <h2 className="font-heading text-3xl md:text-4xl text-white">ACQUISITION SUPPORT</h2>
          </div>
          <div className="grid md:grid-cols-5 gap-6">
            {acquisitionProcess.map((step, index) => (
              <div key={index} className="relative text-center" data-testid={`process-step-${index}`}>
                <span className="text-6xl font-heading text-[#c5a04a] opacity-20 absolute top-0 left-1/2 transform -translate-x-1/2">
                  {step.step}
                </span>
                <div className="pt-12">
                  <h3 className="font-heading text-lg text-white mb-2">{step.title}</h3>
                  <p className="text-slate-300 text-sm">{step.description}</p>
                </div>
                {index < acquisitionProcess.length - 1 && (
                  <div className="hidden md:block absolute top-16 -right-3">
                    <ChevronRight className="w-6 h-6 text-[#c5a04a]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION ECOSYSTEM */}
      <section className="py-20 bg-white" data-testid="mission-ecosystem">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">INTEGRATION</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d]">MISSION-READY AVIATION ECOSYSTEM</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {missionEcosystem.map((item, index) => (
              <Link key={index} to={item.link}>
                <Card className="card-tactical h-full cursor-pointer" data-testid={`ecosystem-${index}`}>
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center mb-4">
                      <item.icon className="w-6 h-6 text-[#1a365d]" />
                    </div>
                    <h3 className="font-heading text-lg text-[#1a365d] mb-2">{item.title}</h3>
                    <p className="text-slate-600 text-sm mb-4">{item.description}</p>
                    <span className="text-[#c5a04a] text-sm flex items-center gap-1">
                      Learn More <ChevronRight className="w-4 h-4" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* INQUIRY FORMS */}
      <section id="request-aircraft" className="py-20 bg-slate-50 border-t border-slate-200" data-testid="inquiry-forms">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">GET STARTED</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d]">AIRCRAFT INQUIRY</h2>
          </div>

          <Tabs defaultValue="buy" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="buy" className="font-heading">REQUEST AN AIRCRAFT</TabsTrigger>
              <TabsTrigger value="sell" className="font-heading" id="sell-aircraft">SELL YOUR AIRCRAFT</TabsTrigger>
            </TabsList>

            {/* BUY FORM */}
            <TabsContent value="buy">
              <Card className="card-tactical">
                <CardContent className="p-8">
                  <form onSubmit={handleBuySubmit} className="space-y-6" data-testid="buy-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-[#1a365d] font-medium">Full Name *</Label>
                        <Input
                          value={buyForm.name}
                          onChange={(e) => setBuyForm({ ...buyForm, name: e.target.value })}
                          required
                          className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#1a365d] font-medium">Organization / Company</Label>
                        <Input
                          value={buyForm.organization}
                          onChange={(e) => setBuyForm({ ...buyForm, organization: e.target.value })}
                          className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-[#1a365d] font-medium">Email *</Label>
                        <Input
                          type="email"
                          value={buyForm.email}
                          onChange={(e) => setBuyForm({ ...buyForm, email: e.target.value })}
                          required
                          className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#1a365d] font-medium">Phone</Label>
                        <Input
                          type="tel"
                          value={buyForm.phone}
                          onChange={(e) => setBuyForm({ ...buyForm, phone: e.target.value })}
                          className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <Label className="text-[#1a365d] font-medium">Aircraft Type Needed</Label>
                        <Select value={buyForm.aircraft_type} onValueChange={(v) => setBuyForm({ ...buyForm, aircraft_type: v })}>
                          <SelectTrigger className="rounded-sm border-2 border-slate-200">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="private-jet">Private Jet</SelectItem>
                            <SelectItem value="turboprop">Turboprop</SelectItem>
                            <SelectItem value="helicopter">Helicopter</SelectItem>
                            <SelectItem value="cargo">Cargo Aircraft</SelectItem>
                            <SelectItem value="amphibious">Amphibious</SelectItem>
                            <SelectItem value="special-mission">Special Mission</SelectItem>
                            <SelectItem value="not-sure">Not Sure</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#1a365d] font-medium">Budget Range</Label>
                        <Select value={buyForm.budget} onValueChange={(v) => setBuyForm({ ...buyForm, budget: v })}>
                          <SelectTrigger className="rounded-sm border-2 border-slate-200">
                            <SelectValue placeholder="Select budget" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="under-1m">Under $1M</SelectItem>
                            <SelectItem value="1-5m">$1M – $5M</SelectItem>
                            <SelectItem value="5-15m">$5M – $15M</SelectItem>
                            <SelectItem value="15-50m">$15M – $50M</SelectItem>
                            <SelectItem value="50m-plus">$50M+</SelectItem>
                            <SelectItem value="lease-charter">Lease / Charter</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#1a365d] font-medium">Timeline</Label>
                        <Select value={buyForm.timeline} onValueChange={(v) => setBuyForm({ ...buyForm, timeline: v })}>
                          <SelectTrigger className="rounded-sm border-2 border-slate-200">
                            <SelectValue placeholder="Select timeline" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="asap">ASAP</SelectItem>
                            <SelectItem value="30-days">30 Days</SelectItem>
                            <SelectItem value="60-90-days">60–90 Days</SelectItem>
                            <SelectItem value="3-6-months">3–6 Months</SelectItem>
                            <SelectItem value="6-plus-months">6+ Months</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#1a365d] font-medium">Mission Description</Label>
                      <Textarea
                        value={buyForm.mission_description}
                        onChange={(e) => setBuyForm({ ...buyForm, mission_description: e.target.value })}
                        rows={4}
                        className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d] resize-none"
                        placeholder="Describe your intended use, range requirements, passenger/cargo needs, or any special requirements..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#1a365d] font-medium">Preferred Contact Method</Label>
                      <Select value={buyForm.contact_method} onValueChange={(v) => setBuyForm({ ...buyForm, contact_method: v })}>
                        <SelectTrigger className="rounded-sm border-2 border-slate-200 w-full md:w-1/3">
                          <SelectValue placeholder="Select preference" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="phone">Phone</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      type="submit"
                      disabled={buyLoading}
                      className="w-full btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm"
                    >
                      {buyLoading ? "Submitting..." : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Submit Aircraft Request
                        </>
                      )}
                    </Button>

                    <p className="text-center text-slate-500 text-xs">
                      Government and contractor inquiries welcome.<br />
                      AMR Global will respond within 24–48 hours.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* SELL FORM */}
            <TabsContent value="sell">
              <Card className="card-tactical">
                <CardContent className="p-8">
                  <p className="text-slate-600 text-sm mb-6 text-center">
                    Discreet representation for aircraft owners. All inquiries handled confidentially.
                  </p>
                  <form onSubmit={handleSellSubmit} className="space-y-6" data-testid="sell-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-[#1a365d] font-medium">Full Name *</Label>
                        <Input
                          value={sellForm.name}
                          onChange={(e) => setSellForm({ ...sellForm, name: e.target.value })}
                          required
                          className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#1a365d] font-medium">Organization / Company</Label>
                        <Input
                          value={sellForm.organization}
                          onChange={(e) => setSellForm({ ...sellForm, organization: e.target.value })}
                          className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-[#1a365d] font-medium">Email *</Label>
                        <Input
                          type="email"
                          value={sellForm.email}
                          onChange={(e) => setSellForm({ ...sellForm, email: e.target.value })}
                          required
                          className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#1a365d] font-medium">Phone</Label>
                        <Input
                          type="tel"
                          value={sellForm.phone}
                          onChange={(e) => setSellForm({ ...sellForm, phone: e.target.value })}
                          className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-[#1a365d] font-medium">Aircraft Type *</Label>
                        <Input
                          value={sellForm.aircraft_type}
                          onChange={(e) => setSellForm({ ...sellForm, aircraft_type: e.target.value })}
                          required
                          placeholder="e.g., Cessna Citation XLS+"
                          className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#1a365d] font-medium">Year</Label>
                        <Input
                          value={sellForm.year}
                          onChange={(e) => setSellForm({ ...sellForm, year: e.target.value })}
                          placeholder="e.g., 2018"
                          className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <Label className="text-[#1a365d] font-medium">Total Hours</Label>
                        <Input
                          value={sellForm.hours}
                          onChange={(e) => setSellForm({ ...sellForm, hours: e.target.value })}
                          placeholder="e.g., 2,500"
                          className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#1a365d] font-medium">Asking Price</Label>
                        <Input
                          value={sellForm.asking_price}
                          onChange={(e) => setSellForm({ ...sellForm, asking_price: e.target.value })}
                          placeholder="e.g., $8,500,000"
                          className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#1a365d] font-medium">Location</Label>
                        <Input
                          value={sellForm.location}
                          onChange={(e) => setSellForm({ ...sellForm, location: e.target.value })}
                          placeholder="e.g., KNEW, New Orleans"
                          className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d]"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#1a365d] font-medium">Additional Notes</Label>
                      <Textarea
                        value={sellForm.notes}
                        onChange={(e) => setSellForm({ ...sellForm, notes: e.target.value })}
                        rows={4}
                        className="rounded-sm border-2 border-slate-200 focus:border-[#1a365d] resize-none"
                        placeholder="Recent maintenance, upgrades, reason for sale, or any relevant details..."
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={sellLoading}
                      className="w-full btn-tactical btn-primary text-sm"
                    >
                      {sellLoading ? "Submitting..." : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Submit Aircraft for Review
                        </>
                      )}
                    </Button>

                    <p className="text-center text-slate-500 text-xs">
                      All inquiries handled with discretion.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 bg-[#1a365d]" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-white mb-4">
            MISSION-READY AVIATION SOURCING
          </h2>
          <p className="text-slate-200 mb-6">
            Veteran-Owned. Confidential. Government and contractor inquiries welcome.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:504-319-7683" className="flex items-center gap-2 text-slate-300 hover:text-[#c5a04a] transition-colors">
              <Phone className="w-5 h-5" />
              <span>504-319-7683</span>
            </a>
            <a href="mailto:AMREID@AMRGLOBALHOLDINGS.COM" className="flex items-center gap-2 text-slate-300 hover:text-[#c5a04a] transition-colors">
              <Mail className="w-5 h-5" />
              <span>AMREID@AMRGLOBALHOLDINGS.COM</span>
            </a>
          </div>
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="py-6 bg-slate-100" data-testid="disclaimer">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-slate-500 text-xs leading-relaxed">
            AMR Global Holdings LLC provides aircraft brokerage and consulting services and may work 
            in coordination with licensed brokers, operators, and aviation partners where required. 
            All transactions comply with applicable aviation regulations. Government and contractor 
            inquiries welcome.
          </p>
        </div>
      </section>
    </div>
  );
};

export default AircraftBrokeragePage;
